import { useCallback } from "react";
import { useApi } from "../useApi";
import { isErr } from "../../Utils/Result";

/**
 * Adds the page the user is on to the agent context, so the backend can pick
 * the answer for that screen. Read at send time: the chat may stay open while
 * the user moves between pages.
 *
 * @param agentContext the context the caller already sends, if any
 * @returns the context including `currentPagePath`
 */
const withCurrentPage = (agentContext?: Record<string, unknown>): Record<string, unknown> => ({
  ...agentContext,
  currentPagePath: window.location.pathname,
});

export const useChat = () => {
  const {
    createChatEndpoint,
    getChatByIdEndpoint,
    sendMessagesEndpoint,
    sendDocumentEndpoint,
    getAllChatsEndpoint,
    setMessageFeedbackEndpoint,
  } = useApi();

  const createChat = useCallback(async (message: string, agentContext?: Record<string, unknown>) => {
    const response = await createChatEndpoint(message, withCurrentPage(agentContext));

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [createChatEndpoint]);

  const getChatById = useCallback(async (chatId: string) => {
    const response = await getChatByIdEndpoint(chatId);

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [getChatByIdEndpoint]);

  const sendMessage = useCallback(async (chatId: string, message: string, agentContext?: Record<string, unknown>) => {
    const response = await sendMessagesEndpoint(chatId, message, withCurrentPage(agentContext));

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [sendMessagesEndpoint]);

  const sendDocument = useCallback(async (chatId: string | undefined, files: File[], text?: string) => {
    const response = await sendDocumentEndpoint(chatId, files, text);

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [sendDocumentEndpoint]);

  const getAllChats = useCallback(async () => {
    const response = await getAllChatsEndpoint();

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [getAllChatsEndpoint]);

  const setMessageFeedback = useCallback(async (chatId: string, messageId: string, feedback: 1 | -1 | 0) => {
    const response = await setMessageFeedbackEndpoint(chatId, messageId, feedback);

    if (isErr(response)) {
      return response;
    }

    return response;
  }, [setMessageFeedbackEndpoint]);

  return {
    createChat,
    getChatById,
    sendMessage,
    sendDocument,
    getAllChats,
    setMessageFeedback,
  };
};
